import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { Build } from '@/utils/api-pb';

interface DeckToolbarProps {
  build: Build;
  version: number | null;
  slideCount: number;
  isBusy: boolean;
  hasContent: boolean;
  /** True while the PDF is being rendered in the browser — it takes a few seconds. */
  isExporting: boolean;
  onBack: () => void;
  onOpenVersions: () => void;
  onFullscreen: () => void;
  onRegenerate: () => void;
  onExportPdf: () => void;
  onShare: () => void;
}

/**
 * Everything above the deck preview.
 *
 * Shorter than the landing toolbar by design: a deck has no address to reserve, no
 * lead form to configure and no publish state to explain — what it can be taken to
 * (show it, save it, share it, rebuild it, look at the history, go back) is all here.
 */
export function DeckToolbar({
  build,
  version,
  slideCount,
  isBusy,
  hasContent,
  isExporting,
  onBack,
  onOpenVersions,
  onFullscreen,
  onRegenerate,
  onExportPdf,
  onShare,
}: DeckToolbarProps) {
  const { t } = useTranslation('builder');

  return (
    <div className="flex flex-wrap items-center gap-[12px]">
      <Button variant="brand-outline" size="brand-sm" onClick={onBack}>
        {t('deck.editor.back')}
      </Button>

      <h1 className="font-manrope text-[18px] font-bold text-[color:var(--black-text,#0d0d0d)]">
        {build.name}
      </h1>

      {version !== null && (
        <span className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {t('editor.version', { n: version })}
        </span>
      )}

      {slideCount > 0 && (
        <span className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {t('deck.editor.slidesCount', { count: slideCount })}
        </span>
      )}

      <div className="ml-auto flex flex-wrap items-center gap-[8px]">
        <Button variant="brand-soft" size="brand-sm" onClick={onOpenVersions}>
          {t('deck.editor.versions')}
        </Button>
        <Button variant="brand-soft" size="brand-sm" onClick={onRegenerate} disabled={isBusy}>
          {t('deck.editor.regenerate')}
        </Button>
        <Button
          variant="brand-soft"
          size="brand-sm"
          onClick={onExportPdf}
          disabled={!hasContent || isExporting}
        >
          {isExporting ? t('deck.editor.exporting') : t('deck.editor.pdf')}
        </Button>
        <Button variant="brand-soft" size="brand-sm" onClick={onShare} disabled={!hasContent}>
          {t('deck.editor.share')}
        </Button>
        <Button variant="brand" size="brand" onClick={onFullscreen} disabled={!hasContent}>
          {t('deck.editor.fullscreen')}
        </Button>
      </div>
    </div>
  );
}
